import { useFormik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Alert,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import * as Yup from "yup";
import { toast } from "react-toastify";

import { StudentSubscribablePlansSelect } from "../../../../components";
import { ActivityButton } from "../../../../components/Common";
import {
  LocalizedProperty,
  ManualPurchaseRequest,
  PaginateQuery,
  ParsedError,
  PhoneDto,
} from "../../../../interfaces";
import { EducationalStageDto } from "../../../../interfaces/curriculum";
import i18n from "../../../../i18n";
import { ErrorHandlingTypeEnum, PaymentThroughEnum } from "../../../../enums";
import { useGetCourseSelectListQuery } from "../../../../store/api/courseApi";
import { useGetCurriculumSelectListQuery } from "../../../../store/api/curriculumApi";
import { useGetEducationSystemSelectListQuery } from "../../../../store/api/educationSystemApi";
import { useGetEducationalStagesQuery } from "../../../../store/api/educationalStageApi";
import { useManualSubscriptionSubjectMutation } from "../../../../store/api/subscriptionApi";

type TProps = {
  userId: string;
  show: boolean;
  onCloseClick: (event: any) => void;
  name: LocalizedProperty | null;
  phoneNumber: PhoneDto;
};

const labelOf = (
  item:
    | { value?: { en?: string; ar?: string }; name?: { en?: string; ar?: string } }
    | undefined,
  langKey: "en" | "ar",
) =>
  item?.value?.[langKey] ||
  item?.value?.en ||
  item?.name?.[langKey] ||
  item?.name?.en ||
  "";

export const ManualSubscribePlanModal: React.FC<TProps> = ({
  userId,
  show,
  onCloseClick,
  name,
  phoneNumber,
}) => {
  const { t } = useTranslation();
  const langKey = i18n.language === "ar" ? "ar" : "en";

  const [educationSystemId, setEducationSystemId] = useState("");
  const [curriculumId, setCurriculumId] = useState("");
  const [courseId, setCourseId] = useState("");
  const [stageId, setStageId] = useState("");
  const [gradeId, setGradeId] = useState("");
  const [selectedPlan, setSelectedPlan] = useState<any>();
  const [hierarchyReady, setHierarchyReady] = useState(false);

  const [
    manualSubscription,
    {
      isLoading: isLoadingManualSubscription,
      error: errorManualSubscription,
      isError: isErrorManualSubscription,
      isSuccess: isSuccessManualSubscription,
    },
  ] = useManualSubscriptionSubjectMutation();

  const { data: educationSystems } = useGetEducationSystemSelectListQuery(
    undefined,
    { skip: !show },
  );
  const { data: curricula } = useGetCurriculumSelectListQuery(
    educationSystemId || undefined,
    { skip: !show || !educationSystemId },
  );
  const { data: courses } = useGetCourseSelectListQuery(undefined, {
    skip: !show,
  });

  const stagesQuery = useMemo(() => {
    const q = new PaginateQuery();
    q.limit = 1000;
    q.page = 1;
    q.sortBy = "sequence_number:ASC";
    q.filter = {
      curriculumId,
      courseId,
    };
    return q;
  }, [curriculumId, courseId]);

  const { data: stagesResponse } = useGetEducationalStagesQuery(stagesQuery, {
    skip: !show || !curriculumId || !courseId,
  });
  const stages: EducationalStageDto[] = stagesResponse?.data || [];

  const selectedStage = useMemo(
    () => stages.find((s) => s.id === stageId),
    [stages, stageId],
  );
  const grades = selectedStage?.grades || [];

  const form = useFormik({
    enableReinitialize: true,
    initialValues: {
      courseId: "",
      gradeId: "",
      subjects: [] as string[],
      invoiceId: null as string | null,
      amount: 0 as number | string,
      currency: "KD",
      paymentThrough: "",
    },
    validationSchema: Yup.object({
      courseId: Yup.string().required(
        t("isNotEmpty", {
          displayName: "course",
        }),
      ),
      gradeId: Yup.string().required(
        t("isNotEmpty", {
          displayName: "grade",
        }),
      ),
      subjects: Yup.array(),
      invoiceId: Yup.string()
        .nullable()
        .when("paymentThrough", {
          is: PaymentThroughEnum.MYFATOORAH,
          then: (schema) =>
            schema.required(
              t("isNotEmpty", {
                displayName: "invoiceId",
              }),
            ),
          otherwise: (schema) => schema.optional().nullable(),
        }),
      paymentThrough: Yup.string().required(),
      amount: Yup.string().when("paymentThrough", {
        is: "OTHER",
        then: () =>
          Yup.number().required(
            t("isNotEmpty", {
              displayName: "amount",
            }),
          ),
      }),
      currency: Yup.string().when("paymentThrough", {
        is: "OTHER",
        then: () =>
          Yup.string().required(
            t("isNotEmpty", {
              displayName: "currency",
            }),
          ),
      }),
    }),
    onSubmit: async (values) => {
      if (!selectedPlan) {
        return;
      }
      const transactionReference = values.invoiceId?.trim() || null;
      const request: ManualPurchaseRequest = {
        invoiceId:
          values.paymentThrough === PaymentThroughEnum.OTHER
            ? transactionReference
            : values.invoiceId,
        productId: [selectedPlan.value],
        userId: userId,
        paymentThrough: values.paymentThrough as PaymentThroughEnum,
        amount: Number(values.amount || 0),
        currency:
          values.paymentThrough === PaymentThroughEnum.OTHER
            ? values.currency || "KD"
            : values.currency,
      };
      await manualSubscription(request).unwrap();
    },
  });

  const resetHierarchy = () => {
    setEducationSystemId("");
    setCurriculumId("");
    setCourseId("");
    setStageId("");
    setGradeId("");
    setHierarchyReady(false);
  };

  const onClose = () => {
    form.resetForm();
    resetHierarchy();
    setSelectedPlan(undefined);
    onCloseClick(!show);
  };

  const onChangePlan = (changedValue: any) => {
    setSelectedPlan(changedValue);
  };

  const clearPlan = () => setSelectedPlan(undefined);

  // Auto-select cascade
  useEffect(() => {
    if (!show) return;
    if (!educationSystems?.length) return;
    if (
      educationSystemId &&
      educationSystems.some((s: any) => s.id === educationSystemId)
    ) {
      return;
    }
    setEducationSystemId(educationSystems[0].id);
  }, [show, educationSystems, educationSystemId]);

  useEffect(() => {
    if (!show) return;
    if (!curricula?.length) {
      if (curriculumId) setCurriculumId("");
      return;
    }
    if (curriculumId && curricula.some((c: any) => c.id === curriculumId)) {
      return;
    }
    setCurriculumId(curricula[0].id);
  }, [show, curricula, curriculumId]);

  useEffect(() => {
    if (!show) return;
    if (!courses?.length) return;
    if (courseId && courses.some((c: any) => c.id === courseId)) return;
    setCourseId(courses[0].id);
  }, [show, courses, courseId]);

  useEffect(() => {
    if (!show) return;
    if (!stages.length) {
      if (stageId) setStageId("");
      return;
    }
    if (stageId && stages.some((s) => s.id === stageId)) return;
    const firstWithGrades = stages.find((s) => s.grades?.length > 0);
    setStageId(firstWithGrades?.id || stages[0].id);
  }, [show, stages, stageId]);

  useEffect(() => {
    if (!show) return;
    if (!grades.length) {
      if (gradeId) setGradeId("");
      return;
    }
    if (gradeId && grades.some((g) => g.id === gradeId)) return;
    setGradeId(grades[0].id);
  }, [show, grades, gradeId]);

  // Sync hierarchy → formik (drives plan query + validation)
  useEffect(() => {
    if (!show) return;
    form.setFieldValue("courseId", courseId || "");
  }, [show, courseId]);

  useEffect(() => {
    if (!show) return;
    form.setFieldValue("gradeId", gradeId || "");
    if (hierarchyReady) {
      clearPlan();
    } else if (gradeId) {
      setHierarchyReady(true);
    }
  }, [show, gradeId]);

  useEffect(() => {
    if (!show) {
      resetHierarchy();
      setSelectedPlan(undefined);
      form.resetForm();
    }
  }, [show]);

  useEffect(() => {
    if (isLoadingManualSubscription || !isErrorManualSubscription) return;
    const parsed = errorManualSubscription as ParsedError;
    if (parsed?.type == ErrorHandlingTypeEnum.FORM) {
      form.setErrors(parsed.error);
      return;
    }
    const message = (errorManualSubscription as { data?: { message?: string } })
      ?.data?.message;
    toast.error(message || "An error occurred", { autoClose: 3000 });
  }, [isLoadingManualSubscription, isErrorManualSubscription]);

  useEffect(() => {
    if (!isLoadingManualSubscription && isSuccessManualSubscription) {
      toast.success(t("manualPurchaseSuccess"), {
        autoClose: 3000,
      });
      onClose();
    }
  }, [isLoadingManualSubscription, isSuccessManualSubscription]);

  const pathLabel = [
    labelOf(
      educationSystems?.find((s: any) => s.id === educationSystemId),
      langKey,
    ),
    labelOf(
      curricula?.find((c: any) => c.id === curriculumId),
      langKey,
    ),
    labelOf(
      courses?.find((c: any) => c.id === courseId),
      langKey,
    ),
    selectedStage?.name?.[langKey] || selectedStage?.name?.en,
    grades.find((g) => g.id === gradeId)?.name?.[langKey] ||
      grades.find((g) => g.id === gradeId)?.name?.en,
  ]
    .filter(Boolean)
    .join(" / ");

  return (
    <Modal
      id="manualSubscription"
      isOpen={show}
      toggle={() => {
        onClose();
      }}
      centered
      size="lg"
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
          return false;
        }}
        action="#"
      >
        <ModalHeader
          className="p-3"
          toggle={() => {
            onClose();
          }}
        >
          {t("addSubscriptionManually")}
        </ModalHeader>

        <ModalBody>
          <div className="mb-3">
            <p>
              <strong>{t("name")}</strong>
              {": "}
              {name && name.en && name.ar
                ? i18n.language === "ar"
                  ? name.ar
                  : name.en
                : "n/a"}
            </p>
            <strong>
              {t("phoneNumber")} {": "}
            </strong>
            <a
              style={{ cursor: "pointer" }}
              href={`tel:+${phoneNumber?.countryCode}${phoneNumber?.phoneNumber}`}
            >
              <u dir="ltr">{`+${phoneNumber?.countryCode}${phoneNumber?.phoneNumber}`}</u>
            </a>
          </div>

          <Row className="g-2 mb-2">
            <Col md={4}>
              <Label className="form-label">
                {t("educationSystem")}
                <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                value={educationSystemId}
                onChange={(e) => {
                  setEducationSystemId(e.target.value);
                  setCurriculumId("");
                  setStageId("");
                  setGradeId("");
                  clearPlan();
                }}
              >
                {!educationSystems?.length ? (
                  <option value="">{t("selectEducationSystem")}</option>
                ) : null}
                {(educationSystems || []).map((s: any) => (
                  <option key={s.id} value={s.id}>
                    {labelOf(s, langKey)}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={4}>
              <Label className="form-label">
                {t("curriculumItem")}
                <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                value={curriculumId}
                disabled={!educationSystemId}
                onChange={(e) => {
                  setCurriculumId(e.target.value);
                  setStageId("");
                  setGradeId("");
                  clearPlan();
                }}
              >
                {!curricula?.length ? (
                  <option value="">{t("selectCurriculum")}</option>
                ) : null}
                {(curricula || []).map((c: any) => (
                  <option key={c.id} value={c.id}>
                    {labelOf(c, langKey)}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={4}>
              <Label className="form-label">
                {t("course")}
                <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                id="courseId"
                name="courseId"
                value={courseId}
                onChange={(e) => {
                  setCourseId(e.target.value);
                  setStageId("");
                  setGradeId("");
                  clearPlan();
                }}
                onBlur={form.handleBlur}
                invalid={
                  !!(form.touched.courseId && form.errors.courseId)
                }
              >
                {!courses?.length ? (
                  <option value="">{t("selectCourse")}</option>
                ) : null}
                {(courses || []).map((c: any) => (
                  <option key={c.id} value={c.id}>
                    {labelOf(c, langKey)}
                  </option>
                ))}
              </Input>
              {form.touched.courseId && form.errors.courseId ? (
                <FormFeedback type="invalid">
                  {form.errors.courseId}
                </FormFeedback>
              ) : null}
            </Col>
          </Row>

          <Row className="g-2 mb-2">
            <Col md={6}>
              <Label className="form-label">
                {t("educationalStages")}
                <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                value={stageId}
                disabled={!curriculumId || !courseId}
                onChange={(e) => {
                  setStageId(e.target.value);
                  setGradeId("");
                  clearPlan();
                }}
              >
                {!stages.length ? (
                  <option value="">{t("selectEducationalStage")}</option>
                ) : null}
                {stages.map((stage) => (
                  <option key={stage.id} value={stage.id}>
                    {stage.name[langKey] || stage.name.en}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={6}>
              <Label className="form-label">
                {t("grade")}
                <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                id="gradeId"
                name="gradeId"
                value={gradeId}
                disabled={!stageId}
                onChange={(e) => {
                  setGradeId(e.target.value);
                  clearPlan();
                }}
                onBlur={form.handleBlur}
                invalid={!!(form.touched.gradeId && form.errors.gradeId)}
              >
                {!grades.length ? (
                  <option value="">{t("selectGrade")}</option>
                ) : null}
                {grades.map((grade) => (
                  <option key={grade.id} value={grade.id}>
                    {grade.name[langKey] || grade.name.en}
                  </option>
                ))}
              </Input>
              {form.touched.gradeId && form.errors.gradeId ? (
                <FormFeedback type="invalid">
                  {form.errors.gradeId}
                </FormFeedback>
              ) : null}
            </Col>
          </Row>

          {pathLabel ? (
            <small className="text-muted d-block mb-3">{pathLabel}</small>
          ) : null}

          <Alert color="info" className="mb-3">
            {t("manualSubscribeUpgradeHint")}
          </Alert>
          <div className="mb-3">
            <Label htmlFor="planId" className="form-label">
              {t("plans")}
              <span className="text-danger">*</span>
            </Label>
            <StudentSubscribablePlansSelect
              placeholder="filterByPlan"
              isMulti={false}
              closeMenuOnSelect={false}
              value={selectedPlan}
              onChange={onChangePlan}
              courseId={courseId}
              gradeId={gradeId}
              userId={userId}
              id="planId"
            />
          </div>
          <div className="mb-3">
            <label className="form-label">
              {t("paymentThrough")}
              <span className="text-danger">*</span>
            </label>
            <div>
              <div className="form-check form-check-inline">
                <Input
                  className="form-check-input"
                  type="radio"
                  name="inlineRadioOptions"
                  id="inlineRadio1"
                  value={PaymentThroughEnum.MYFATOORAH}
                  checked={
                    form.values.paymentThrough === PaymentThroughEnum.MYFATOORAH
                  }
                  onChange={() => {
                    form.setFieldValue("invoiceId", "");
                    form.setFieldValue("amount", "");
                    form.setFieldValue("currency", "");
                    form.setFieldValue(
                      "paymentThrough",
                      PaymentThroughEnum.MYFATOORAH,
                    );
                  }}
                />
                <label className="form-check-label" htmlFor="inlineRadio1">
                  {t(PaymentThroughEnum.MYFATOORAH)}
                </label>
              </div>
              <div className="form-check form-check-inline">
                <Input
                  className="form-check-input"
                  type="radio"
                  name="inlineRadioOptions"
                  id="inlineRadio2"
                  value={PaymentThroughEnum.OTHER}
                  checked={
                    form.values.paymentThrough === PaymentThroughEnum.OTHER
                  }
                  onChange={() => {
                    form.setFieldValue("invoiceId", "");
                    form.setFieldValue("amount", "0");
                    form.setFieldValue("currency", "KD");
                    form.setFieldValue(
                      "paymentThrough",
                      PaymentThroughEnum.OTHER,
                    );
                  }}
                />
                <label className="form-check-label" htmlFor="inlineRadio2">
                  {t(PaymentThroughEnum.OTHER)}
                </label>
              </div>
            </div>
            {form.touched.paymentThrough && form.errors.paymentThrough ? (
              <FormFeedback type="invalid ">
                {form.errors.paymentThrough as string}
              </FormFeedback>
            ) : null}
          </div>
          {[
            PaymentThroughEnum.OTHER.toString(),
            PaymentThroughEnum.MYFATOORAH.toString(),
          ].includes(form.values.paymentThrough) && (
            <>
              <div className="mb-3">
                <label htmlFor="invoiceId" className="form-label">
                  {form.values.paymentThrough === PaymentThroughEnum.MYFATOORAH
                    ? t("invoiceId")
                    : t("transactionReference")}
                  {form.values.paymentThrough ===
                  PaymentThroughEnum.MYFATOORAH ? (
                    <span className="text-danger">*</span>
                  ) : null}
                </label>
                <Input
                  autoComplete="off"
                  id="invoiceId"
                  name="invoiceId"
                  value={form.values.invoiceId || ""}
                  type="text"
                  className="form-control"
                  maxLength={25}
                  placeholder={
                    form.values.paymentThrough === PaymentThroughEnum.MYFATOORAH
                      ? t("enterInvoiceId")
                      : t("enterTransactionReference")
                  }
                  onChange={(e) => {
                    e.preventDefault();
                    const { value } = e.target;
                    if (form.values.paymentThrough === "MYFATOORAH") {
                      const regex =
                        /^(0*[0-9][0-9]*(\[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
                      if (regex.test(value.toString())) {
                        form.setFieldValue("invoiceId", value);
                      } else {
                        form.setFieldValue("invoiceId", "");
                      }
                    } else {
                      form.setFieldValue("invoiceId", value);
                    }
                  }}
                  onBlur={form.handleBlur}
                  invalid={
                    form.touched.invoiceId && form.errors.invoiceId
                      ? true
                      : false
                  }
                />
                {form.touched.invoiceId && form.errors.invoiceId ? (
                  <FormFeedback type="invalid">
                    {form.errors.invoiceId as string}
                  </FormFeedback>
                ) : null}
              </div>
              {form.values.paymentThrough === "OTHER" && (
                <>
                  <div className="mb-3">
                    <label htmlFor="amount" className="form-label">
                      {t("price")}
                      <span className="text-danger">*</span>
                    </label>
                    <Input
                      onChange={(e) => {
                        e.preventDefault();
                        const { value } = e.target;
                        const regex =
                          /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
                        if (regex.test(value.toString())) {
                          form.setFieldValue("amount", value);
                        } else {
                          form.setFieldValue("amount", "");
                        }
                      }}
                      autoComplete="off"
                      id="amount"
                      name="amount"
                      value={form.values.amount}
                      type="text"
                      className="form-control"
                      placeholder={t("price")}
                      onBlur={form.handleBlur}
                      invalid={
                        form.touched.amount && form.errors.amount ? true : false
                      }
                    />
                    {form.touched.amount && form.errors.amount ? (
                      <FormFeedback type="invalid">
                        {form.errors.amount as string}
                      </FormFeedback>
                    ) : null}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="currency" className="form-label">
                      {t("currency")}
                      <span className="text-danger">*</span>
                    </label>
                    <Input
                      invalid={
                        form.touched.currency && form.errors.currency
                          ? true
                          : false
                      }
                      type="select"
                      id="currency"
                      name="currency"
                      value={form.values.currency}
                      className="form-select form-select-md"
                      onBlur={form.handleBlur}
                      onChange={form.handleChange}
                    >
                      <option value="">{t("currency")}</option>
                      <option value="KD">KD</option>
                      <option value="USD">USD</option>
                    </Input>
                    {form.touched.currency && form.errors.currency ? (
                      <FormFeedback type="invalid">
                        {form.errors.currency as string}
                      </FormFeedback>
                    ) : null}
                  </div>
                </>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Link
            to={"#"}
            onClick={onClose}
            className="btn btn-link link-success fw-medium"
          >
            <i className="ri-close-line me-1 align-middle"></i> {t("close")}
          </Link>
          <ActivityButton
            title={t("save")}
            inProgressTitle="saving"
            color="success"
            className="btn btn-success"
            type="submit"
            inProgress={isLoadingManualSubscription}
            disabled={
              isLoadingManualSubscription ||
              !courseId ||
              !gradeId ||
              !selectedPlan
            }
          />
        </ModalFooter>
      </Form>
    </Modal>
  );
};
